import React from "react";
import { Layers, Tag, Activity, Sparkles, GitBranch, Bone } from "lucide-react";

/**
 * Layer toggles for the body diagram.
 *
 * `muscles`, `treatmentMap`, and `labels` drive the legacy SVG diagram.
 * `skeletal`, `trigger`, `referral` open a realistic anatomy overlay panel
 * (AnatomyViewer) inline below the workspace with the matching tab active.
 * These three behave as a mutually-exclusive set — selecting one turns the
 * others off — so the panel always renders a single layer at a time.
 */
const LAYERS = [
  { id: "muscles", label: "Muscles", icon: Layers, ready: true },
  { id: "treatmentMap", label: "Treatment Map", icon: Sparkles, ready: true },
  { id: "labels", label: "Labels on hover", icon: Tag, ready: true },
  { id: "skeletal", label: "Skeletal landmarks", icon: Bone, ready: true },
  { id: "trigger", label: "Trigger points", icon: Activity, ready: true },
  { id: "referral", label: "Referral patterns", icon: GitBranch, ready: true },
];

// IDs in this set act as a single-pick group: turning one ON turns the others OFF.
const EXCLUSIVE_LAYERS = new Set(["skeletal", "trigger", "referral"]);

export default function LayerToggles({ value = {}, onChange }) {
  const toggle = (id) => {
    const next = { ...value };
    if (EXCLUSIVE_LAYERS.has(id)) {
      const turningOn = !value[id];
      EXCLUSIVE_LAYERS.forEach((k) => { next[k] = false; });
      next[id] = turningOn;
    } else {
      next[id] = !value[id];
    }
    onChange?.(next);
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="layer-toggles">
      <span className="label-overline text-ink-500 mr-1">Layers:</span>
      {LAYERS.map((l) => {
        const Icon = l.icon;
        const on = !!value[l.id];
        const disabled = !l.ready;
        return (
          <button
            key={l.id}
            type="button"
            onClick={() => !disabled && toggle(l.id)}
            disabled={disabled}
            data-testid={`layer-toggle-${l.id}`}
            title={disabled ? "Coming soon" : `${on ? "Hide" : "Show"} ${l.label}`}
            className={`min-h-[32px] px-3 rounded-full text-[11px] font-medium inline-flex items-center gap-1.5 transition-all border ${
              disabled
                ? "bg-paper-100 text-ink-400 border-paper-200 cursor-not-allowed"
                : on
                ? "bg-ink-900 text-white border-ink-900 hover:bg-ink-800"
                : "bg-white text-ink-700 border-paper-200 hover:bg-paper-100"
            }`}
          >
            <Icon className="w-3 h-3" />
            {l.label}
          </button>
        );
      })}
    </div>
  );
}

export const DEFAULT_LAYERS = {
  muscles: true,
  treatmentMap: true,
  labels: false,
  skeletal: false,
  trigger: false,
  referral: false,
};
